import { Button, Center, Flex, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { UTBReportTemplate } from '@e-origin/utb-report-template';

import Logo from '@assets/images/logo192.png';
import { useRequestUtbReport, useRetrieveUTBReportData } from '@hooks/batch';
import { useDeclarant } from '@hooks/declarant';
import { useRetrieveDeclarantLogo } from '@hooks/declarant/useRetrieveDeclarantLogo';
import { useSettings } from '@hooks/useSettings';
import { classes } from '@styles';
import { useTranslation } from './useTranslation';

interface UTBReportProps {
  batchId: string;
}

// eslint-disable-next-line max-statements
export const UTBReport = ({ batchId }: UTBReportProps) => {
  const { data: settings } = useSettings();
  const [page, setPage] = useState(0);
  const { data: reportData } = useRetrieveUTBReportData(batchId, page);
  const [needsPagination, setNeedsPagination] = useState(false);
  const { t } = useTranslation();
  const { data: declarant } = useDeclarant();
  const [selectedGroup, setSelectedGroup] = useState(null);

  const { data: declarantLogo } = useRetrieveDeclarantLogo(declarant?._id, declarant?.logo ? true : false);
  const requestReport = useRequestUtbReport();

  useEffect(() => {
    if (reportData?.totalLRNs) {
      setNeedsPagination(reportData.totalPages > 1);

      if (reportData.groups.length > 0) {
        setSelectedGroup(reportData.groups[0]);
      }
    }
  }, [reportData]);

  if (!reportData) {
    return (
      <Center style={{ flex: 1 }}>
        <Text>Loading report data...</Text>
      </Center>
    );
  }

  return (
    <Flex gap="md" style={{ flex: 1, minHeight: 0 }}>
      <Paper withBorder p="md" radius="md" style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Stack mb="md" gap="xs">
          <Button onClick={() => requestReport.mutate({ batchId })} disabled={requestReport.isPending} loading={requestReport.isPending} size="sm">
            Download ZIP
          </Button>
        </Stack>

        {needsPagination && (
          <Flex justify="space-between" align="center" mb="md">
            <Button type="button" variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <IconArrowLeft />
            </Button>
            {`${page + 1}/${reportData.totalPages}`}
            <Button type="button" variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === reportData.totalPages - 1}>
              <IconArrowRight />
            </Button>
          </Flex>
        )}

        <ScrollArea offsetScrollbars="y" style={{ flex: 1, minHeight: 0 }}>
          <Stack gap="md">
            {reportData.groups.map((groups) => (
              <Paper
                key={groups.lrn}
                withBorder
                p="md"
                className={classes.cursorPointer}
                bg={selectedGroup?.lrn === groups.lrn ? '#f0f0f0' : 'white'}
                onClick={() => setSelectedGroup(groups)}
              >
                <Text size="sm">LRN: {groups.lrn}</Text>
              </Paper>
            ))}
          </Stack>
        </ScrollArea>
      </Paper>

      <ScrollArea offsetScrollbars="y" style={{ flex: 1, minHeight: 0 }}>
        <Paper withBorder p="md" radius="md">
          {selectedGroup ? (
            <UTBReportTemplate
              declarant={declarant}
              logo={declarantLogo || Logo}
              utbDeclarant={reportData.declarant}
              representative={reportData.representative}
              importer={selectedGroup.importer}
              group={selectedGroup}
              t={t}
              country={settings.country}
            />
          ) : (
            <Text>Select a declaration from the sidebar</Text>
          )}
        </Paper>
      </ScrollArea>
    </Flex>
  );
};
